import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Users,
  ChatCircleDots,
  BookOpen,
  Sparkle,
  WhatsappLogo,
  FilePdf,
} from "@phosphor-icons/react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WhatsAppFab from "../components/WhatsAppFab";
import { openWhatsapp } from "../lib/whatsapp";

const OPTIONS = [
  {
    n: "01",
    icon: Sparkle,
    accent: "#D9381E",
    title: "Try the ₹999 Live Plan",
    desc:
      "10 trading days of live support — our daily trade plan, futures levels, ranges on WhatsApp, and access to EOD discussion sessions. The cheapest way to see exactly how we trade.",
    cta: "Join the ₹999 live plan",
    to: "/community",
    external: false,
  },
  {
    n: "02",
    icon: Users,
    accent: "#0A0A0A",
    title: "Speak to alumni & current batch members",
    desc:
      "We'll personally connect you with previous students and members of the currently running cohort. Ask them anything — the good, the bad, the honest.",
    cta: "Request alumni intro on WhatsApp",
    isWhatsApp: true,
    waMessage:
      "Hi, I'm considering the Great Eagle Tech course. Please connect me with an alumni / current batch member so I can hear their experience directly.",
  },
  {
    n: "03",
    icon: ChatCircleDots,
    accent: "#3B5BDB",
    title: "Premium Community — trial access",
    desc:
      "We can add you to our Premium Trading Community on a trial basis. Interact with existing members personally, watch how the group operates, and clarify your doubts directly with them.",
    cta: "Request community trial on WhatsApp",
    isWhatsApp: true,
    waMessage:
      "Hi, I'd like a trial add to your Premium Trading Community so I can experience it before deciding on a course package.",
  },
  {
    n: "04",
    icon: BookOpen,
    accent: "#B37A00",
    title: "3-day demo — training library access",
    desc:
      "Three days of access to our Student Portal training library. Experience our live market approach, analysis, trade planning and community support before you commit.",
    cta: "Request 3-day demo on WhatsApp",
    isWhatsApp: true,
    waMessage:
      "Hi, please give me 3-day demo access to the Great Eagle Tech training library so I can experience your live market approach before enrolling.",
  },
  {
    n: "05",
    icon: FilePdf,
    accent: "#25D366",
    title: "Read the full syllabus first",
    desc:
      "Download the complete 12-module syllabus PDF — every topic, every phase, every deliverable. No sign-up needed. Skim it and you'll know exactly what to expect.",
    cta: "↓ Download Syllabus PDF",
    isDownload: true,
    downloadHref: "/downloads/GreatEagle_Syllabus.pdf",
  },
];

export default function Decide() {
  return (
    <div
      data-testid="decide-root"
      className="min-h-screen bg-[#F4F3ED] text-[#0A0A0A] font-body"
    >
      <Navbar />
      <main className="pt-28 lg:pt-32 pb-16">
        <section className="px-5 lg:px-10 max-w-[1320px] mx-auto">
          <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-3">
            <Link to="/" className="hover:text-[#0A0A0A]">
              Home
            </Link>{" "}
            / Try before you commit
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.9] mb-5">
            Try us before <br />
            you <span className="text-[#D9381E]">commit.</span>
          </h1>
          <p className="font-body text-base lg:text-lg text-[#4A4A4A] max-w-2xl mb-4">
            Before you make your decision, we want you to have complete clarity
            and confidence about what our ecosystem offers.
          </p>
          <p className="font-body text-base lg:text-lg text-[#4A4A4A] max-w-2xl mb-12">
            We don't want you to join based only on what we tell you.{" "}
            <strong className="text-[#0A0A0A]">
              Experience the community, speak with our members, understand how
              we work — and then decide.
            </strong>
          </p>
        </section>

        <section className="px-5 lg:px-10 max-w-[1320px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {OPTIONS.map((o) => {
              const Icon = o.icon;
              return (
                <div
                  key={o.n}
                  data-testid={`decide-card-${o.n}`}
                  className="border border-[#0A0A0A] bg-[#F4F3ED] p-7 lg:p-9 flex flex-col"
                >
                  <div className="flex items-start justify-between mb-6">
                    <span
                      className="font-mono-acc text-[11px] tracking-[0.3em] font-bold"
                      style={{ color: o.accent }}
                    >
                      {o.n}
                    </span>
                    <Icon
                      size={30}
                      weight="duotone"
                      style={{ color: o.accent }}
                    />
                  </div>
                  <h3 className="font-display font-black text-2xl lg:text-3xl tracking-tighter leading-tight mb-3">
                    {o.title}
                  </h3>
                  <p className="font-body text-[15px] text-[#4A4A4A] leading-relaxed mb-7 flex-1">
                    {o.desc}
                  </p>
                  {o.isDownload ? (
                    <a
                      href={o.downloadHref}
                      download
                      data-testid={`decide-cta-${o.n}`}
                      className="inline-flex items-center justify-center gap-2 self-start bg-[#0A0A0A] text-[#F4F3ED] px-5 py-3 font-body text-sm uppercase tracking-[0.22em] hover:bg-[#D9381E] transition-colors"
                    >
                      {o.cta}
                    </a>
                  ) : o.isWhatsApp ? (
                    <button
                      onClick={() => openWhatsapp(o.waMessage)}
                      data-testid={`decide-cta-${o.n}`}
                      className="inline-flex items-center justify-center gap-2 self-start bg-[#25D366] text-[#0A0A0A] px-5 py-3 font-body text-sm uppercase tracking-[0.22em] font-bold hover:bg-[#1ebd5b] transition-colors"
                    >
                      <WhatsappLogo size={16} weight="fill" /> {o.cta}
                    </button>
                  ) : (
                    <Link
                      to={o.to}
                      data-testid={`decide-cta-${o.n}`}
                      className="inline-flex items-center justify-center gap-2 self-start bg-[#0A0A0A] text-[#F4F3ED] px-5 py-3 font-body text-sm uppercase tracking-[0.22em] hover:bg-[#D9381E] transition-colors"
                    >
                      {o.cta} <ArrowRight size={14} weight="bold" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section className="px-5 lg:px-10 max-w-[1320px] mx-auto mt-16">
          <div
            data-testid="decide-summary"
            className="border border-[#0A0A0A] bg-[#0A0A0A] text-[#F4F3ED] p-8 lg:p-12"
          >
            <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#FFB703] mb-3">
              Ready to decide?
            </div>
            <h2 className="font-display font-black text-3xl lg:text-4xl tracking-tighter mb-3">
              See the packages side by side.
            </h2>
            <p className="font-body text-[#A1A1AA] max-w-2xl mb-6">
              Once you've experienced the community and spoken to a few
              members, come back and pick the package that fits how you want to
              learn.
            </p>
            <Link
              to="/packages"
              data-testid="decide-see-packages"
              className="inline-flex items-center gap-2 bg-[#D9381E] text-white px-6 py-3.5 font-body text-sm uppercase tracking-[0.22em] hover:bg-[#F4F3ED] hover:text-[#0A0A0A] transition-colors"
            >
              Compare packages <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
